/**
 * Shared filter state for the "รายงานรวม" (Supervisor Reporting) tab.
 * When SUPERVISOR_NURSE views this tab, filters at the top control both Dashboard and Table.
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { getReportingMonth, type ReportingMonth } from '../lib/reportingMonth';
import {
  NURSE_NOTES_DEFAULT_KPI_ID,
  type NurseNotesOfficialKpiId,
} from '../lib/nurseNotesKpiMetrics';
import type { NurseNotesComparisonMode } from '../lib/nurseNotesComparisonData';

export type { ReportingMonth };
export type DashboardViewMode = 'monthly' | 'trimester' | 'year';
export type DataSourceFilter = 'all' | 'own' | 'crossAudit';

export type NurseNotesComparisonModeOrNone = NurseNotesComparisonMode | 'none';

export type SupervisorReportingFiltersValue = {
  categoryId: string;
  setCategoryId: (id: string) => void;
  ethicDeptFilter: string[];
  setEthicDeptFilter: (ids: string[]) => void;
  medErrorDeptFilter: string[];
  setMedErrorDeptFilter: (ids: string[]) => void;
  /** nurse_notes on รายงานรวม: empty = all departments in scope */
  nurseNotesDeptFilter: string[];
  setNurseNotesDeptFilter: (ids: string[]) => void;
  dateFrom: string;
  setDateFrom: (v: string) => void;
  dateTo: string;
  setDateTo: (v: string) => void;
  positionFilter: string;
  setPositionFilter: (v: string) => void;
  tenureFilter: string;
  setTenureFilter: (v: string) => void;
  // nurse_notes filters
  view: DashboardViewMode;
  setView: (v: DashboardViewMode) => void;
  reportingMonth: ReportingMonth;
  setReportingMonth: (v: ReportingMonth) => void;
  selectedMetric: string;
  setSelectedMetric: (v: string) => void;
  dataSourceFilter: DataSourceFilter;
  setDataSourceFilter: (v: DataSourceFilter) => void;
  /** Official nurse_notes KPI used by shared dashboard charts (narrow union). */
  nurseNotesOfficialKpiId: NurseNotesOfficialKpiId;
  setNurseNotesOfficialKpiId: (id: NurseNotesOfficialKpiId) => void;
  /** nurse_notes comparison panel; `'none'` hides the panel. */
  nurseNotesComparisonMode: NurseNotesComparisonModeOrNone;
  setNurseNotesComparisonMode: (mode: NurseNotesComparisonModeOrNone) => void;
  loadTrigger: number;
  triggerLoad: () => void;
  metricKeys: string[];
  setMetricKeys: (keys: string[]) => void;
  loading: boolean;
  setLoading: (v: boolean) => void;
};

const SupervisorReportingFiltersContext = createContext<SupervisorReportingFiltersValue | null>(null);

export function useSupervisorReportingFilters(): SupervisorReportingFiltersValue | null {
  return useContext(SupervisorReportingFiltersContext);
}

type SupervisorReportingFiltersProviderProps = {
  children: React.ReactNode;
  defaultCategoryId?: string;
  /** When set (e.g. `/report?category=nurse_notes`), sync filter category — keeps shell nav + deep links aligned */
  syncCategoryFromQuery?: string | null;
};

const REPORTING_SHELL_CATEGORY_IDS = new Set([
  'nurse_notes',
  'ethicSurveys',
  'orgClimateSurveys',
  'siren_bundle',
  'med_error',
  'med_error_incident',
  'fall_incident',
]);

export function SupervisorReportingFiltersProvider({
  children,
  defaultCategoryId = 'ethicSurveys',
  syncCategoryFromQuery,
}: SupervisorReportingFiltersProviderProps) {
  const [categoryId, setCategoryId] = useState(defaultCategoryId);
  const [ethicDeptFilter, setEthicDeptFilterState] = useState<string[]>([]);
  const [medErrorDeptFilter, setMedErrorDeptFilterState] = useState<string[]>([]);
  const [nurseNotesDeptFilter, setNurseNotesDeptFilterState] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [tenureFilter, setTenureFilter] = useState('');
  const [view, setView] = useState<DashboardViewMode>('year');
  const [reportingMonth, setReportingMonth] = useState<ReportingMonth>(() =>
    getReportingMonth(new Date()),
  );
  const [selectedMetric, setSelectedMetric] = useState('monthly_pct');
  const [dataSourceFilter, setDataSourceFilter] = useState<DataSourceFilter>('all');
  const [nurseNotesOfficialKpiId, setNurseNotesOfficialKpiId] = useState<NurseNotesOfficialKpiId>(
    NURSE_NOTES_DEFAULT_KPI_ID,
  );
  const [nurseNotesComparisonMode, setNurseNotesComparisonMode] =
    useState<NurseNotesComparisonModeOrNone>('none');
  const [loadTrigger, setLoadTrigger] = useState(0);

  React.useEffect(() => {
    if (categoryId === 'med_error_incident') {
      setMedErrorDeptFilterState([]);
    }
    if (categoryId !== 'nurse_notes') {
      setNurseNotesDeptFilterState([]);
      setNurseNotesOfficialKpiId(NURSE_NOTES_DEFAULT_KPI_ID);
      setNurseNotesComparisonMode('none');
    }
  }, [categoryId]);

  React.useEffect(() => {
    if (!syncCategoryFromQuery || !REPORTING_SHELL_CATEGORY_IDS.has(syncCategoryFromQuery)) return;
    setCategoryId(syncCategoryFromQuery);
  }, [syncCategoryFromQuery]);

  const [metricKeys, setMetricKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const setEthicDeptFilter = useCallback((ids: string[]) => {
    setEthicDeptFilterState(ids);
  }, []);

  const setMedErrorDeptFilter = useCallback((ids: string[]) => {
    setMedErrorDeptFilterState(ids);
  }, []);

  const setNurseNotesDeptFilter = useCallback((ids: string[]) => {
    setNurseNotesDeptFilterState(ids);
  }, []);

  const triggerLoad = useCallback(() => {
    setLoadTrigger((n) => n + 1);
  }, []);

  /**
   * When category, view, or reporting month changes, bump loadTrigger so Dashboard / ReportingTable
   * run the same refresh path as the manual "ดึงข้อมูล" button (see Dashboard sharedLoadTrigger effect).
   * Skip the first run so initial load stays driven by each component's loadData effect only.
   */
  const skipSharedFilterLoadBumpRef = React.useRef(true);
  React.useEffect(() => {
    if (skipSharedFilterLoadBumpRef.current) {
      skipSharedFilterLoadBumpRef.current = false;
      return;
    }
    setLoadTrigger((n) => n + 1);
  }, [categoryId, view, reportingMonth]);

  const value: SupervisorReportingFiltersValue = {
    categoryId,
    setCategoryId,
    ethicDeptFilter,
    setEthicDeptFilter,
    medErrorDeptFilter,
    setMedErrorDeptFilter,
    nurseNotesDeptFilter,
    setNurseNotesDeptFilter,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    positionFilter,
    setPositionFilter,
    tenureFilter,
    setTenureFilter,
    view,
    setView,
    reportingMonth,
    setReportingMonth,
    selectedMetric,
    setSelectedMetric,
    dataSourceFilter,
    setDataSourceFilter,
    nurseNotesOfficialKpiId,
    setNurseNotesOfficialKpiId,
    nurseNotesComparisonMode,
    setNurseNotesComparisonMode,
    loadTrigger,
    triggerLoad,
    metricKeys,
    setMetricKeys,
    loading,
    setLoading,
  };

  return (
    <SupervisorReportingFiltersContext.Provider value={value}>
      {children}
    </SupervisorReportingFiltersContext.Provider>
  );
}
